import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  EventCategory,
  EventLocationType,
  EventVisibility,
  EventStatus,
  type EventInput,
} from "@vedaunion/shared";
import {
  useCreateEvent,
  useEvent,
  useUpdateEvent,
  useDeleteEvent,
  useDuplicateEvent,
} from "../../lib/events";
import { useCan, useMe } from "../../lib/auth";
import { ApiError } from "../../lib/api";
import {
  detectTimezone,
  listTimezones,
  localStringToUtc,
  utcToLocalString,
} from "../../lib/tz";
import {
  type RecurrenceState,
  NO_RECURRENCE,
  parseRecurrence,
  serialiseRecurrence,
} from "../../lib/recurrence";
import { RecurrencePicker } from "../../components/calendar/RecurrencePicker";
import { MediaPicker } from "../../components/editor/MediaPicker";
import { DocumentReferencePicker } from "../../components/document/DocumentReferencePicker";
import { useSetEventDocuments } from "../../lib/documents";

/**
 * Event create/edit form. Section-divided so authors can scan the form on a
 * single screen at typical desktop widths. Owns:
 *
 *   When  — start, end, all-day, timezone, recurrence (with live preview)
 *   Where — location type, address, online URL
 *   Who   — visibility, capacity
 *   What  — title, summary, description, category, cover image
 *   State — status (draft / published / cancelled), permission-gated
 *
 * Datetimes round-trip through `localStringToUtc` / `utcToLocalString` so the
 * naïve `<input type="datetime-local">` value is interpreted in the event's
 * chosen timezone, not the browser's local time. Without this, picking
 * 18:00 in Europe/Ljubljana would silently save as 18:00 UTC.
 */

const FormSchema = z
  .object({
    title: z.string().trim().min(1, "Title is required.").max(200),
    summary: z.string().trim().max(280).optional(),
    description: z.string().max(20000).optional(),
    category: EventCategory,
    /** Naïve wall-clock string in the chosen `timezone`. */
    startsAt: z.string().min(1, "Start is required."),
    endsAt: z.string().min(1, "End is required."),
    allDay: z.boolean(),
    timeTbd: z.boolean(),
    timezone: z.string().min(1).max(64),
    locationType: EventLocationType,
    location: z.string().trim().max(255).optional(),
    onlineUrl: z.string().trim().max(500).optional(),
    coverImage: z.string().trim().max(500).optional(),
    capacity: z.union([z.coerce.number().int().positive().max(100000), z.literal("")]).optional(),
    visibility: EventVisibility,
    status: EventStatus,
  })
  .refine((v) => Date.parse(v.endsAt) >= Date.parse(v.startsAt), {
    path: ["endsAt"],
    message: "End must be at or after start.",
  });

type FormValues = z.infer<typeof FormSchema>;

function nowInTzString(timezone: string, offsetHours = 0): string {
  const now = new Date(Date.now() + offsetHours * 3600 * 1000);
  return utcToLocalString(now, timezone);
}

export default function EventForm({ mode }: { mode: "create" | "edit" }) {
  const navigate = useNavigate();
  const params = useParams<{ slug: string }>();
  const [search] = useSearchParams();
  const me = useMe();
  const canEditAny = useCan("events.edit_any");
  const canPublish = useCan("events.publish");
  const canCancel = useCan("events.cancel");
  const canCreate = useCan("events.create");

  const eventQ = useEvent(mode === "edit" ? params.slug : undefined);
  const ev = eventQ.data?.event ?? null;
  const create = useCreateEvent();
  const update = useUpdateEvent(ev?.id ?? 0);
  const remove = useDeleteEvent();
  const duplicate = useDuplicateEvent();

  const initialTz = detectTimezone() || "Europe/Ljubljana";
  const whenParam = search.get("when");
  const initialStart = whenParam
    ? utcToLocalString(new Date(whenParam), initialTz) || nowInTzString(initialTz)
    : nowInTzString(initialTz);
  const initialEnd = whenParam
    ? (() => {
        const start = new Date(whenParam);
        if (Number.isFinite(start.getTime())) {
          return utcToLocalString(new Date(start.getTime() + 60 * 60 * 1000), initialTz) || nowInTzString(initialTz, 1);
        }
        return nowInTzString(initialTz, 1);
      })()
    : nowInTzString(initialTz, 1);
  const defaults: FormValues = {
    title: "",
    summary: "",
    description: "",
    category: "class",
    startsAt: initialStart,
    endsAt: initialEnd,
    allDay: false,
    timeTbd: false,
    timezone: initialTz,
    locationType: "in-person",
    location: "",
    onlineUrl: "",
    coverImage: "",
    capacity: "" as unknown as number,
    visibility: "public",
    status: "draft",
  };

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: defaults,
  });

  const { register, handleSubmit, reset, control, formState, setError, watch, setValue } = form;
  const [recurrence, setRecurrence] = useState<RecurrenceState>(NO_RECURRENCE);
  const [coverPickerOpen, setCoverPickerOpen] = useState(false);

  // Hydrate when editing
  useEffect(() => {
    if (mode !== "edit" || !ev) return;
    reset({
      title: ev.title,
      summary: ev.summary ?? "",
      description: ev.description ?? "",
      category: ev.category,
      startsAt: utcToLocalString(ev.startsAt, ev.timezone),
      endsAt: utcToLocalString(ev.endsAt, ev.timezone),
      allDay: ev.allDay,
      timeTbd: ev.timeTbd,
      timezone: ev.timezone,
      locationType: ev.locationType,
      location: ev.location ?? "",
      onlineUrl: ev.onlineUrl ?? "",
      coverImage: ev.coverImage ?? "",
      capacity: (ev.capacity ?? "") as unknown as number,
      visibility: ev.visibility,
      status: ev.status,
    });
    setRecurrence(parseRecurrence(ev.recurrenceRule, new Date(ev.startsAt)));
  }, [mode, ev, reset]);

  const isOwn = !!me.data?.user && ev?.createdBy === me.data.user.id;
  const canEditThis = mode === "create" || isOwn || canEditAny;

  const tzList = useMemo(() => listTimezones(), []);

  const onSubmit = async (values: FormValues) => {
    try {
      const startsUtc = localStringToUtc(values.startsAt, values.timezone);
      const endsUtc = localStringToUtc(values.endsAt, values.timezone);
      const recurrenceRule = serialiseRecurrence(recurrence);
      const recurrenceEndsAt = recurrence.until ?? null;
      const payload: EventInput = {
        title: values.title,
        summary: values.summary || null,
        description: values.description || null,
        category: values.category,
        startsAt: startsUtc.toISOString(),
        endsAt: endsUtc.toISOString(),
        allDay: values.allDay,
        timeTbd: values.timeTbd,
        timezone: values.timezone,
        locationType: values.locationType,
        location: values.location || null,
        onlineUrl: values.onlineUrl ? values.onlineUrl : null,
        coverImage: values.coverImage || null,
        capacity:
          typeof values.capacity === "number"
            ? values.capacity
            : (values.capacity as string) === ""
              ? null
              : Number(values.capacity),
        visibility: values.visibility,
        status: values.status,
        recurrenceRule,
        recurrenceEndsAt,
      };

      if (mode === "create") {
        const res = await create.mutateAsync(payload);
        navigate(`/dashboard/calendar/${res.event.slug}`);
      } else if (ev) {
        const res = await update.mutateAsync(payload);
        navigate(`/dashboard/calendar/${res.event.slug}`);
      }
    } catch (err) {
      const e = err as ApiError;
      setError("root", { type: "server", message: e?.body?.message ?? e?.message ?? "Save failed." });
    }
  };

  const onDelete = async () => {
    if (!ev) return;
    if (!window.confirm("Delete this event? This cannot be undone.")) return;
    try {
      await remove.mutateAsync(ev.id);
      navigate("/dashboard/calendar");
    } catch (err) {
      const e = err as ApiError;
      window.alert(e?.body?.message ?? e?.message ?? "Delete failed.");
    }
  };

  const onDuplicate = async () => {
    if (!ev) return;
    try {
      const res = await duplicate.mutateAsync(ev.id);
      navigate(`/dashboard/calendar/${res.event.slug}/edit`);
    } catch (err) {
      const e = err as ApiError;
      window.alert(e?.body?.message ?? e?.message ?? "Duplicate failed.");
    }
  };

  if (mode === "edit" && eventQ.isLoading) {
    return <p className="container-edit">Loading…</p>;
  }
  if (mode === "edit" && !ev) {
    return <p className="container-edit">Event not found.</p>;
  }
  if (mode === "edit" && !canEditThis) {
    return <p className="container-edit">You don't have permission to edit this event.</p>;
  }

  const locationType = watch("locationType");
  const startsAt = watch("startsAt");
  const tz = watch("timezone");
  const allDay = watch("allDay");
  const recurrenceAnchor = useMemo(() => {
    try {
      return startsAt ? localStringToUtc(startsAt, tz) : new Date();
    } catch {
      return new Date();
    }
  }, [startsAt, tz]);

  const allowedStatuses: ("draft" | "published" | "cancelled")[] = ["draft"];
  if (canPublish || ev?.status === "published") allowedStatuses.push("published");
  if (canCancel || ev?.status === "cancelled") allowedStatuses.push("cancelled");

  return (
    <div className="container-edit max-w-[920px]">
      <header className="flex items-baseline justify-between gap-3 flex-wrap mb-6">
        <div>
          <p className="page-eyebrow">{mode === "create" ? "New event" : "Edit event"}</p>
          <h1 className="page-title">
            {mode === "create" ? "Add an event to the calendar." : ev?.title}
          </h1>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <button type="button" className="btn-app-ghost" onClick={() => navigate(-1)}>
            Cancel
          </button>
          {mode === "edit" && canCreate && (
            <button type="button" className="btn-app-ghost" onClick={onDuplicate}>
              Duplicate
            </button>
          )}
        </div>
      </header>

      <form onSubmit={handleSubmit(onSubmit)} className="event-form" noValidate>
        {/* WHAT — title, summary, description, category, cover image */}
        <section className="form-section">
          <p className="form-section-title">What</p>
          <Field label="Title" error={formState.errors.title?.message}>
            <input className="auth-input" type="text" autoComplete="off" {...register("title")} />
          </Field>

          <Field label="Summary" hint="One sentence shown in lists." error={formState.errors.summary?.message}>
            <input className="auth-input" type="text" {...register("summary")} />
          </Field>

          <Field label="Description" hint="Plain text. Longer detail." error={formState.errors.description?.message}>
            <textarea className="auth-input" rows={5} {...register("description")} />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Category" error={formState.errors.category?.message}>
              <select className="auth-input" {...register("category")}>
                {EventCategory.options.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </Field>
            <Field label="Cover image" hint="Optional. Upload or paste a URL.">
              <Controller
                control={control}
                name="coverImage"
                render={({ field }) => (
                  <>
                    {field.value ? (
                      <div className="event-cover-preview">
                        <img src={field.value} alt="" />
                        <div className="event-cover-actions">
                          <button type="button" className="row-action" onClick={() => setCoverPickerOpen((o) => !o)}>
                            {coverPickerOpen ? "Close" : "Replace"}
                          </button>
                          <button type="button" className="row-action" onClick={() => field.onChange("")}>
                            Remove
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="image-placeholder image-placeholder-action"
                        onClick={() => setCoverPickerOpen(true)}
                      >
                        <span className="font-grotesk text-[0.85rem] text-[var(--color-ink-soft)]">
                          Click to upload — or paste a URL.
                        </span>
                      </button>
                    )}
                    {(coverPickerOpen || !field.value) && (
                      <div className="mt-2">
                        <MediaPicker
                          kind="image"
                          currentUrl={field.value || ""}
                          onPick={(url) => {
                            field.onChange(url);
                            setCoverPickerOpen(false);
                          }}
                          onCancel={field.value ? () => setCoverPickerOpen(false) : undefined}
                        />
                      </div>
                    )}
                  </>
                )}
              />
            </Field>
          </div>
        </section>

        {/* WHEN — start, end, all-day, timezone, recurrence */}
        <section className="form-section">
          <p className="form-section-title">When</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Starts" error={formState.errors.startsAt?.message}>
              <input
                className="auth-input"
                type={allDay ? "date" : "datetime-local"}
                {...register("startsAt", {
                  setValueAs: (v: string) => {
                    if (!v) return v;
                    return v.includes("T") ? v : `${v}T00:00`;
                  },
                })}
              />
            </Field>
            <Field label="Ends" error={formState.errors.endsAt?.message}>
              <input
                className="auth-input"
                type={allDay ? "date" : "datetime-local"}
                {...register("endsAt", {
                  setValueAs: (v: string) => {
                    if (!v) return v;
                    return v.includes("T") ? v : `${v}T23:59`;
                  },
                })}
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="All-day">
              <Controller
                control={control}
                name="allDay"
                render={({ field }) => (
                  <label className="auth-input flex items-center gap-3 cursor-pointer" style={{ background: "var(--color-vellum-dim)" }}>
                    <input
                      type="checkbox"
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                        if (e.target.checked) {
                          // Snap times to the day boundaries on toggle.
                          const s = watch("startsAt").split("T")[0];
                          const en = watch("endsAt").split("T")[0];
                          if (s) setValue("startsAt", `${s}T00:00`);
                          if (en) setValue("endsAt", `${en}T23:59`);
                        }
                      }}
                      style={{ width: "auto" }}
                    />
                    <span className="font-grotesk text-[0.85rem]">{field.value ? "All-day event" : "Has specific times"}</span>
                  </label>
                )}
              />
            </Field>
            <Field label="Timezone" hint="Used to interpret the times above and to display the event.">
              <input
                className="auth-input"
                type="text"
                list="tz-suggestions"
                {...register("timezone")}
              />
              <datalist id="tz-suggestions">
                {tzList.map((t) => (
                  <option key={t} value={t} />
                ))}
              </datalist>
            </Field>
          </div>

          {!allDay && (
            <Field
              label="Time"
              hint="The date is set but the time isn't yet. The calendar shows “Time TBA” instead of the time above."
            >
              <label className="auth-input flex items-center gap-3 cursor-pointer" style={{ background: "var(--color-vellum-dim)" }}>
                <input type="checkbox" {...register("timeTbd")} style={{ width: "auto" }} />
                <span className="font-grotesk text-[0.85rem]">Time to be announced</span>
              </label>
            </Field>
          )}

          <div className="form-section-sub">
            <RecurrencePicker state={recurrence} onChange={setRecurrence} anchor={recurrenceAnchor} />
          </div>
        </section>

        {/* WHERE — location type, address, online URL */}
        <section className="form-section">
          <p className="form-section-title">Where</p>
          <Field label="Location type">
            <select className="auth-input" {...register("locationType")}>
              {EventLocationType.options.map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </Field>
          {(locationType === "in-person" || locationType === "hybrid") && (
            <Field label="Address" error={formState.errors.location?.message}>
              <input className="auth-input" type="text" placeholder="House of Unity, Krnjak" {...register("location")} />
            </Field>
          )}
          {(locationType === "online" || locationType === "hybrid") && (
            <Field label="Online URL" hint="Zoom / Jitsi / livestream link." error={formState.errors.onlineUrl?.message}>
              <input className="auth-input" type="url" placeholder="https://" {...register("onlineUrl")} />
            </Field>
          )}
        </section>

        {/* WHO — visibility, capacity */}
        <section className="form-section">
          <p className="form-section-title">Who can come</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Visibility" hint="Public = anyone. Members = signed-in only. Private = creator + editors.">
              <select className="auth-input" {...register("visibility")}>
                {EventVisibility.options.map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
              </select>
            </Field>
            <Field label="Capacity" hint="Max attendees. Leave empty for unlimited." error={formState.errors.capacity?.message}>
              <input className="auth-input" type="number" min={1} {...register("capacity")} />
            </Field>
          </div>
        </section>

        {/* MATERIALS — linked documents (edit-only; we need an event id) */}
        {mode === "edit" && ev && (
          <EventDocumentsSection eventId={ev.id} initialIds={ev.relatedDocuments.map((d) => d.id)} />
        )}

        {/* STATE */}
        <section className="form-section">
          <p className="form-section-title">Status</p>
          <Field label="Status" hint="Draft = hidden from the calendar. Published = visible. Cancelled = struck through.">
            <select className="auth-input" {...register("status")}>
              {allowedStatuses.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </Field>
        </section>

        {formState.errors.root && (
          <p className="auth-error">— {formState.errors.root.message}</p>
        )}

        <div className="form-action-bar">
          <div className="flex flex-wrap gap-2 items-center">
            <button type="submit" className="btn-app" disabled={create.isPending || update.isPending}>
              {create.isPending || update.isPending ? "Saving…" : "Save event"}
            </button>
            <button type="button" className="btn-app-ghost" onClick={() => navigate(-1)}>
              Cancel
            </button>
          </div>
          {mode === "edit" && (canEditAny || isOwn) && (
            <button
              type="button"
              className="btn-app-ghost is-danger"
              onClick={onDelete}
            >
              Delete
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  error,
  hint,
  children,
}: {
  label: string;
  error?: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="auth-field">
      <label className="auth-label">{label}</label>
      {children}
      {error && <p className="auth-error">— {error}</p>}
      {!error && hint && <p className="auth-hint">{hint}</p>}
    </div>
  );
}

/**
 * Linked documents — surfaces a "materials for this event" card on the
 * public event page. Owns its own state + save button so the picker can
 * accept multi-select interactions and the user explicitly commits.
 */
function EventDocumentsSection({
  eventId,
  initialIds,
}: {
  eventId: number;
  initialIds: number[];
}) {
  const [selectedIds, setSelectedIds] = useState<number[]>(initialIds);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const set = useSetEventDocuments(eventId);

  // Re-sync when initialIds change (e.g. after refetch on save).
  useEffect(() => {
    setSelectedIds(initialIds);
  }, [initialIds.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = useMemo(() => {
    if (initialIds.length !== selectedIds.length) return true;
    const a = [...initialIds].sort();
    const b = [...selectedIds].sort();
    return a.some((v, i) => v !== b[i]);
  }, [initialIds, selectedIds]);

  const onSave = async () => {
    await set.mutateAsync(selectedIds);
    setSavedAt(new Date());
  };

  return (
    <section className="form-section">
      <p className="form-section-title">Materials</p>
      <p className="font-grotesk text-[0.78rem] text-[var(--color-ink-soft)] -mt-1 mb-3 max-w-[60ch]">
        Documents linked here surface on the public event page as
        &quot;Materials for this event&quot; — chant scripts, study guides,
        the audio for the practice.
      </p>
      <DocumentReferencePicker
        selectedIds={selectedIds}
        onChange={setSelectedIds}
        hint="Multi-select. Your selections take effect when you click Save below."
      />
      <div className="flex items-center gap-3 mt-3">
        <button
          type="button"
          className="btn-app-ghost"
          onClick={() => void onSave()}
          disabled={!dirty || set.isPending}
        >
          {set.isPending ? "Saving…" : dirty ? "Save materials" : "Saved"}
        </button>
        {savedAt && !dirty && (
          <span className="font-grotesk text-[0.66rem] tracking-[0.16em] uppercase text-[var(--color-ink-mute)]">
            Saved
          </span>
        )}
      </div>
    </section>
  );
}
